import type { Weights } from './evaluate.js';
import type { SearchLimits } from './search.js';
import type { BotDifficulty, BotStrategy } from './types.js';

export interface StrategyInfo {
  name: string;
  description: string;
  weights: Weights;
}

/**
 * Each strategy is a personality: the same search, weighing positions differently.
 * `balanced` follows the v0 greedy order from the spec: win, keep the Beacon safe, capture, climb.
 */
export const STRATEGIES: Readonly<Record<BotStrategy, StrategyInfo>> = {
  balanced: {
    name: 'Balanced',
    description: 'Wins when it can, keeps its Beacon safe, takes pieces, then climbs.',
    weights: {
      material: 100,
      beaconThreat: 4000,
      beaconAttack: 60,
      summitProgress: 25,
      enemySummitProgress: 20,
      guard: 5,
      summitGuard: 15,
      advance: 2,
    },
  },
  summit: {
    name: 'Summiteer',
    description: 'Races its Beacon to the summit and trades pieces to get there.',
    weights: {
      material: 60,
      beaconThreat: 4000,
      beaconAttack: 30,
      summitProgress: 90,
      enemySummitProgress: 15,
      guard: 3,
      summitGuard: 5,
      advance: 0,
    },
  },
  hunter: {
    name: 'Hunter',
    description: 'Pushes its pieces forward and goes after the enemy Beacon.',
    weights: {
      material: 160,
      beaconThreat: 3000,
      beaconAttack: 160,
      summitProgress: 10,
      enemySummitProgress: 15,
      guard: 0,
      summitGuard: 0,
      advance: 12,
    },
  },
  fortress: {
    name: 'Fortress',
    description: 'Holds the ground around the summit and guards its Beacon.',
    weights: {
      material: 100,
      beaconThreat: 6000,
      beaconAttack: 40,
      summitProgress: 15,
      enemySummitProgress: 45,
      guard: 10,
      summitGuard: 60,
      advance: -2,
    },
  },
};

export interface DifficultyInfo {
  name: string;
  description: string;
  /** 1: looks at its own move. 2: also looks at the best reply. */
  depth: 1 | 2;
  /** Chance of playing a random legal move instead. */
  blunder: number;
  /** Moves scoring within this margin of the best are equally likely. */
  margin: number;
  /** When set, replaces `depth`: a full alpha-beta search within these limits. */
  search?: SearchLimits;
}

export const DIFFICULTIES: Readonly<Record<BotDifficulty, DifficultyInfo>> = {
  easy: {
    name: 'Easy',
    description: 'Looks one move ahead, misses a lot.',
    depth: 1,
    blunder: 0.3,
    margin: 150,
  },
  normal: {
    name: 'Normal',
    description: 'Looks one move ahead and takes the best of it.',
    depth: 1,
    blunder: 0,
    margin: 1,
  },
  hard: {
    name: 'Hard',
    description: 'Also looks at your best reply before it moves.',
    depth: 2,
    blunder: 0,
    margin: 1,
  },
  expert: {
    name: 'Expert',
    description: 'Searches several moves ahead and sees exchanges through.',
    depth: 2,
    blunder: 0,
    margin: 0,
    search: { nodes: 6000, maxDepth: 8, quiescence: 4 },
  },
};
